import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = ['fr', 'en', 'ar', 'es', 'de'] as const;

export function LanguageSwitcher({ className = '' }: { className?: string }) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = i18n.resolvedLanguage ?? 'fr';

  return (
    <div ref={containerRef} className={`relative font-manuscript ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-1.5 rounded-full border border-manuscript-line-strong bg-paper-100 px-3 py-1.5 text-sm font-medium text-manuscript-ink-soft hover:bg-paper-200"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span aria-hidden>🌐</span>
        {t(`language.${current}`)}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-manuscript-ink/40 sm:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden
          />
          <div
            role="listbox"
            className="fixed inset-x-4 bottom-4 z-50 overflow-hidden rounded-xl border border-manuscript-line-strong bg-paper-100 shadow-xl sm:absolute sm:inset-x-auto sm:end-0 sm:bottom-auto sm:mt-2 sm:w-40"
          >
            {LANGUAGES.map((lng) => (
              <button
                key={lng}
                type="button"
                role="option"
                aria-selected={current === lng}
                onClick={() => {
                  i18n.changeLanguage(lng);
                  setIsOpen(false);
                }}
                className={`block w-full px-3.5 py-2.5 text-start text-sm font-medium transition-colors ${
                  current === lng ? 'bg-seal-500 text-paper-50' : 'text-manuscript-ink-soft hover:bg-paper-200'
                }`}
              >
                {t(`language.${lng}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
